import React from 'react';
import { Container, Grid, Paper, Typography, Box, Card, CardContent, CardActionArea } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TitleBar from '../../components/TitleBar/TitleBar';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

interface ReportCard {
    id: string;
    title: string;
    description: string;
    path: string;
    icon: React.ReactNode;
    roles?: string[];
}

const Reports = () => {
    const navigate = useNavigate();

    const availableReports: ReportCard[] = [
        {
            id: 'review-entities-by-type',
            title: 'Review Entities by Type',
            description: 'View the distribution of review entities across different types with bar and pie chart visualizations',
            path: '/reports/review-entities-by-type',
            icon: <BarChartIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        },
        {
            id: 'metrics-trending',
            title: 'Metrics Trending',
            description: 'Track how metric values have changed over time by review entity and dimension. Visualize trends and patterns in your project metrics.',
            path: '/reports/metrics-trending',
            icon: <TrendingUpIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
        },
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <TitleBar />
            <Container maxWidth="xl" sx={{ pt: 6, pb: 8 }}>
                <Box sx={{ mb: 6 }}>
                    <Typography 
                        variant="h3" 
                        sx={{ 
                            fontWeight: 700,
                            color: 'text.primary',
                            mb: 2,
                            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                        }}
                    >
                        Reports & Analytics
                    </Typography>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            color: 'text.secondary',
                            fontWeight: 400,
                            maxWidth: 800
                        }}
                    >
                        Explore comprehensive reports and analytics to gain insights into your review entities, metrics, and trends.
                    </Typography>
                </Box>
                
                <Grid container spacing={3}>
                    {availableReports.map((report) => (
                        <Grid item xs={12} sm={6} md={4} key={report.id}>
                            <Card 
                                elevation={2}
                                sx={{
                                    height: '100%',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: '0 12px 24px -8px rgba(0, 0, 0, 0.2)',
                                    }
                                }}
                            >
                                <CardActionArea 
                                    onClick={() => navigate(report.path)}
                                    sx={{ height: '100%' }}
                                >
                                    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                                            {report.icon}
                                        </Box>
                                        <Typography 
                                            variant="h6" 
                                            component="div" 
                                            gutterBottom
                                            sx={{ 
                                                fontWeight: 600,
                                                textAlign: 'center',
                                                mb: 2
                                            }}
                                        >
                                            {report.title}
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary"
                                            sx={{ 
                                                textAlign: 'center',
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {report.description}
                                        </Typography>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {availableReports.length === 0 && (
                    <Paper sx={{ p: 6, textAlign: 'center' }}>
                        <PieChartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                            No reports available at this time
                        </Typography>
                    </Paper>
                )}
            </Container>
        </Box>
    );
};

export default Reports;
